import { Fragment, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import AnalysisCandlestickChart from '../components/AnalysisCandlestickChart';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Clock,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Loader2,
  Search,
  Calendar,
  Timer,
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';

// ---------------------------------------------------------------------------
// SAATLİK ANALİZ SAYFASI — Şu an sadece adminler kullanabilir.
//
// Mimari not (ileride platinum'a açılırsa):
//   - Bu sayfayı ProtectedRoute'a taşı (şu an AdminRoute altında).
//   - Backend endpointlerini /admin/ prefix'inden çıkar.
//   - Backend'e arka planda saatlik sync servisi ekle (bkz. server.py yorumları).
//   - Bu sayfanın UI kodu değişmez, sadece API path ve auth güncellenir.
// ---------------------------------------------------------------------------

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

// ─── Paylaşılan yardımcılar ─────────────────────────────────────────────────

function ScoreBadge({ score }) {
  if (score >= 5)
    return <Badge className="bg-[#6D7C3B]/15 text-[#6D7C3B] border-[#6D7C3B]/30 font-semibold"><TrendingUp className="w-3 h-3 mr-1" />{score.toFixed(2)}</Badge>;
  if (score >= 1)
    return <Badge className="bg-[#C86F4A]/15 text-[#C86F4A] border-[#C86F4A]/30 font-semibold"><TrendingUp className="w-3 h-3 mr-1" />{score.toFixed(2)}</Badge>;
  if (score >= -1)
    return <Badge variant="outline" className="text-[#7A6A5C] font-semibold">{score.toFixed(2)}</Badge>;
  return <Badge className="bg-[#B04832]/15 text-[#B04832] border-[#B04832]/30 font-semibold"><TrendingDown className="w-3 h-3 mr-1" />{score.toFixed(2)}</Badge>;
}

function SimilarityBadge({ pct }) {
  const color = pct >= 80 ? '#6D7C3B' : pct >= 65 ? '#C86F4A' : '#7A6A5C';
  return (
    <span className="font-semibold tabular-nums" style={{ color }}>
      %{pct.toFixed(1)}
    </span>
  );
}

function formatPrice(val) {
  if (val == null) return '—';
  return `₺${Number(val).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatVolume(val) {
  if (val == null) return '—';
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(0)}K`;
  return String(val);
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

// lightweight-charts UTC zaman damgalarını UTC olarak gösterir.
// BIST verisi UTC'de geldiğinden her mumu +3 saat kaydırarak İstanbul saatini gösteriyoruz.
const toIstanbul = (candles) =>
  candles?.map(c => ({ ...c, time: c.time + 10800 })) ?? null;

// ─── Simons saatlik (GÖLGE / deneme) ───────────────────────────────────────

function SubBar({ value }) {
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="h-1.5 flex-1 rounded-full bg-[#E6DCCF] overflow-hidden">
        <div className="h-full rounded-full bg-[#C86F4A]" style={{ width: `${Math.max(2, Math.min(100, value))}%` }} />
      </div>
      <span className="text-xs text-[#7A6A5C] tabular-nums w-8 text-right">%{Math.round(value)}</span>
    </div>
  );
}

function SimonsHourlyPanel({ getAuthHeader }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [side, setSide] = useState('top');
  const [selected, setSelected] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/simons/hourly/list?limit=30`, { headers: getAuthHeader() })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [getAuthHeader]);

  const toggle = async (symbol) => {
    if (selected === symbol) { setSelected(null); setChartData(null); return; }
    setSelected(symbol); setChartLoading(true); setChartData(null);
    try {
      const res = await axios.get(`${API_URL}/stocks/${symbol}/candlestick?interval=1h&period=60d`,
        { headers: getAuthHeader(), timeout: 30_000 });
      setChartData(toIstanbul(res.data?.candles));
    } catch { setChartData(null); }
    finally { setChartLoading(false); }
  };

  const rows = side === 'top' ? data?.top : data?.bottom;
  const v = data?.model?.validation || {};

  return (
    <div className="space-y-4">
      <div className="bg-[#2E2620]/5 border border-[#2E2620]/15 rounded-xl px-4 py-3 space-y-2">
        <p className="text-sm font-semibold text-[#2E2620] flex items-center gap-2">
          Simons Saatlik
          <Badge variant="outline" className="text-[10px] text-[#C86F4A] border-[#C86F4A]/30">Gölge · deneme</Badge>
        </p>
        <p className="text-xs text-[#7A6A5C] leading-relaxed">
          Günlük Simons modelinin saatlik barlarla çalışan kardeşi: mal toplama/dağıtma, geometri ve topoloji ölçümlerinden,
          hissenin önümüzdeki ~1 haftada diğer hisselere göre performansını tahmin eder. Geriye dönük testte (2025–2026, 22 ay)
          22 ayın 22'sinde olumlu; en iyi %10 ortalamadan haftada ~0,5 puan iyi gitti.
        </p>
        <p className="text-xs text-[#B04832] leading-relaxed">
          Henüz kendini gerçek sonuçla kanıtlamadı: yalnızca 22 ay sınandı. Listeler her hafta Sinyal Karnesi'ne kaydedilir ve
          1 hafta sonra ölçülür; birkaç haftalık gerçek sonuç olumlu çıkmadan karar için kullanılmamalıdır.
        </p>
        {data?.model && (
          <p className="text-[11px] text-[#7A6A5C]">
            Model {data.model.version}
            {typeof v.holdout_ic === 'number' ? ` · son 3 ay doğrulama IC ${v.holdout_ic.toFixed(3)}` : ''}
            {data.date ? ` · skor günü ${data.date} (${data.universe} hisse)` : ''}
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <Button size="sm" onClick={() => { setSide('top'); setSelected(null); }}
          className={side === 'top' ? 'bg-[#6D7C3B] hover:bg-[#5C6B2F] text-white' : 'bg-white border border-[#E6DCCF] text-[#7A6A5C] hover:bg-[#F6F1EA]'}>
          En yüksek 30
        </Button>
        <Button size="sm" onClick={() => { setSide('bottom'); setSelected(null); }}
          className={side === 'bottom' ? 'bg-[#B04832] hover:bg-[#963B28] text-white' : 'bg-white border border-[#E6DCCF] text-[#7A6A5C] hover:bg-[#F6F1EA]'}>
          En düşük 30
        </Button>
      </div>

      {loading ? (
        <div className="card-organic p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#C86F4A] mx-auto" /></div>
      ) : !data?.available ? (
        <div className="card-organic p-6 text-sm text-[#7A6A5C]">Saatlik model henüz eğitilmedi.</div>
      ) : !rows?.length ? (
        <div className="card-organic p-6 text-sm text-[#7A6A5C]">Henüz skor yok. Skorlar her sabah hesaplanır.</div>
      ) : (
        <div className="card-organic overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E6DCCF] text-xs text-[#7A6A5C] uppercase tracking-wide">
                  <th className="text-left px-4 py-2.5 font-medium w-10">#</th>
                  <th className="text-left px-4 py-2.5 font-medium">Sembol</th>
                  <th className="text-left px-4 py-2.5 font-medium">Skor</th>
                  <th className="text-left px-4 py-2.5 font-medium">Mal toplama</th>
                  <th className="text-left px-4 py-2.5 font-medium">Geometri</th>
                  <th className="text-left px-4 py-2.5 font-medium">Topoloji</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const isOpen = selected === row.symbol;
                  return (
                    <Fragment key={row.symbol}>
                      <tr onClick={() => toggle(row.symbol)}
                        className={`border-b border-[#E6DCCF] cursor-pointer transition-colors ${isOpen ? 'bg-[#F0E8DC]' : 'hover:bg-[#F6F1EA]'}`}>
                        <td className="px-4 py-3 text-[#7A6A5C] tabular-nums">{idx + 1}</td>
                        <td className="px-4 py-3 font-semibold text-[#2E2620]">{row.symbol}</td>
                        <td className="px-4 py-3">
                          <Badge className={side === 'top'
                            ? 'bg-[#6D7C3B]/15 text-[#6D7C3B] border-[#6D7C3B]/30 font-semibold'
                            : 'bg-[#B04832]/15 text-[#B04832] border-[#B04832]/30 font-semibold'}>
                            %{Math.round(row.score_pct)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3"><SubBar value={row.acc_pct} /></td>
                        <td className="px-4 py-3"><SubBar value={row.geo_pct} /></td>
                        <td className="px-4 py-3"><SubBar value={row.top_pct} /></td>
                        <td className="px-4 py-3 text-right text-[#7A6A5C]">
                          {isOpen ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-[#F6F1EA]">
                          <td colSpan={7} className="px-4 py-4">
                            <p className="text-xs font-semibold text-[#2E2620] mb-2">{row.symbol} — Saatlik Grafik (Son 60 Gün)</p>
                            <AnalysisCandlestickChart data={chartData} loading={chartLoading} height={280} showTime={true} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 1: Momentum Sıralaması ──────────────────────────────────────────────

function MomentumTab({ getAuthHeader }) {
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [data, setData]             = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [chartData, setChartData]   = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  // 'calm': sakin ve dengeli (testte olumlu) · 'momentum': son saatlerde hareketlenenler
  const [view, setView] = useState('calm');

  const runScan = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    setSelectedSymbol(null);
    setChartData(null);
    try {
      const res = await axios.get(
        `${API_URL}/admin/hourly-analysis${forceRefresh ? '?force_refresh=true' : ''}`,
        { headers: getAuthHeader(), timeout: 300_000 }
      );
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Tarama sırasında hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const handleRowClick = async (symbol) => {
    if (selectedSymbol === symbol) { setSelectedSymbol(null); setChartData(null); return; }
    setSelectedSymbol(symbol);
    setChartLoading(true);
    setChartData(null);
    try {
      const res = await axios.get(
        `${API_URL}/stocks/${symbol}/candlestick?interval=1h&period=60d`,
        { headers: getAuthHeader(), timeout: 30_000 }
      );
      setChartData(toIstanbul(res.data?.candles));
    } catch { setChartData(null); }
    finally { setChartLoading(false); }
  };

  return (
    <div className="space-y-4">
      {/* Görünüm seçici */}
      <div className="flex gap-2 flex-wrap">
        <Button
          size="sm"
          variant={view === 'calm' ? 'default' : 'outline'}
          onClick={() => { setView('calm'); setSelectedSymbol(null); }}
          className={view === 'calm' ? 'bg-[#6D7C3B] hover:bg-[#5C6B2F] text-white' : 'border-[#E6DCCF] text-[#7A6A5C]'}
        >
          Sakin ve Dengeli
        </Button>
        <Button
          size="sm"
          variant={view === 'momentum' ? 'default' : 'outline'}
          onClick={() => { setView('momentum'); setSelectedSymbol(null); }}
          className={view === 'momentum' ? 'bg-[#C86F4A] hover:bg-[#B05D3A] text-white' : 'border-[#E6DCCF] text-[#7A6A5C]'}
        >
          Hareketlenenler
        </Button>
        <Button
          size="sm"
          variant={view === 'simons' ? 'default' : 'outline'}
          onClick={() => { setView('simons'); setSelectedSymbol(null); }}
          className={view === 'simons' ? 'bg-[#2E2620] hover:bg-[#1F1915] text-white' : 'border-[#E6DCCF] text-[#7A6A5C]'}
        >
          Simons Saatlik (gölge)
        </Button>
      </div>

      {view === 'simons' ? <SimonsHourlyPanel getAuthHeader={getAuthHeader} /> : (<>

      {view === 'calm' && (
        <div className="bg-[#6D7C3B]/10 border border-[#6D7C3B]/30 rounded-xl px-4 py-3 space-y-2">
          <p className="text-sm font-semibold text-[#2E2620]">Sakin ve Dengeli Hisseler Nedir?</p>
          <p className="text-xs text-[#7A6A5C] leading-relaxed">
            Son 120 saatlik grafiğe iki ölçüyle bakılır: <span className="font-medium text-[#2E2620]">oynaklık</span> (fiyat ne kadar
            sert dalgalanıyor) ve <span className="font-medium text-[#2E2620]">topolojik denge</span> (grafik tek bir baskın yükseliş/düşüş
            yerine dengeli dip ve tepelerden mi oluşuyor). Skor, likit hisseler içindeki yüzdelik sıradır; yüksek = daha sakin ve dengeli.
          </p>
          <p className="text-xs text-[#7A6A5C] leading-relaxed">
            Geriye dönük testte (578 hisse, 2023 sonu–2026) listenin en üst %10'u diğer hisseleri 1 gün, 1 hafta ve 1 ay sonra
            %55–56 oranında geçti; aynı etki 2017–2026 günlük verisinde de görüldü. Küçük ama tutarlı bir avantajdır; tek bir
            hisse için "yükselecek" anlamına gelmez, yatırım tavsiyesi değildir.
          </p>
        </div>
      )}

      {/* Açıklama kutusu */}
      {view === 'momentum' && (
      <div className="bg-[#F6F1EA] border border-[#E6DCCF] rounded-xl px-4 py-3 space-y-2">
        <p className="text-sm font-semibold text-[#2E2620]">Momentum Analizi Nedir?</p>
        <p className="text-xs text-[#7A6A5C] leading-relaxed">
          Momentum analizi, bir hissenin kısa vadeli fiyat hareketinin gücünü ve yönünü ölçer.
          Tüm BIST sembolleri için son 60 günün saatlik verileri Yahoo Finance'tan çekilir ve
          her hisseye bir <span className="font-medium text-[#2E2620]">momentum skoru</span> hesaplanır.
        </p>
        <p className="text-xs text-[#7A6A5C] leading-relaxed">
          <span className="font-medium text-[#2E2620]">Skor nasıl hesaplanır?</span>{' '}
          Üç bileşenin ağırlıklı toplamıdır:
        </p>
        <ul className="text-xs text-[#7A6A5C] space-y-1 pl-3">
          <li>• <span className="font-medium text-[#2E2620]">Hareketli Ortalama Pozisyonu (3×):</span> Son kapanışın 20 saatlik MA'dan yüzde sapması — fiyatın trende göre nerede olduğunu gösterir.</li>
          <li>• <span className="font-medium text-[#2E2620]">Kısa Vadeli Momentum (5×):</span> Son 3 mumun ortalamasının önceki 3 muma göre değişimi — anlık ivmeyi ölçer.</li>
          <li>• <span className="font-medium text-[#2E2620]">Hacim Bonusu:</span> Son hacim, 10 saatlik ortalama hacmin belirgin üzerindeyse skora eklenir — güçlü alım baskısını işaret eder.</li>
        </ul>
        <p className="text-xs text-[#B04832] leading-relaxed">
          Bu liste son saatlerde en çok hareketlenen hisseleri gösterir; alım sinyali değildir. Geriye dönük testte (2 yıllık
          saatlik veri) listenin üstündeki hisseler sonraki saat, gün ve haftada diğer hisseleri geçemedi (%47–49); 1 saatlik
          ufukta hafif geri verme görüldü.
        </p>
      </div>
      )}

      {/* Kontrol satırı */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-[#7A6A5C]">Tüm BIST sembollerinin son 10 günlük saatlik momentum skoru</p>
        <div className="flex items-center gap-2">
          {data && (
            <span className="text-xs text-[#7A6A5C] flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {data.cached ? 'Hafıza' : 'Canlı'} · {formatTime(data.updated_at)}
            </span>
          )}
          {data && !loading && (
            <Button variant="outline" size="sm" onClick={() => runScan(true)} className="border-[#E6DCCF] text-[#7A6A5C]">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Yenile
            </Button>
          )}
          {!data && (
            <Button onClick={() => runScan(false)} disabled={loading} className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white">
              {loading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analiz ediliyor…</>
                : <><TrendingUp className="w-4 h-4 mr-2" />Momentum Taraması Başlat</>}
            </Button>
          )}
        </div>
      </div>

      {loading && (
        <div className="card-organic p-8 text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#C86F4A] mx-auto" />
          <p className="text-[#2E2620] font-medium">Semboller analiz ediliyor…</p>
          <p className="text-xs text-[#7A6A5C]">1300+ sembol taranıyor. İlk çalıştırmada 2-3 dakika sürebilir.</p>
        </div>
      )}

      {error && !loading && (
        <div className="card-organic p-5 flex items-start gap-3 border-[#B04832]/30 bg-[#B04832]/5">
          <AlertCircle className="w-5 h-5 text-[#B04832] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#B04832]">{error}</p>
            <Button variant="ghost" size="sm" onClick={() => runScan(false)} className="mt-2 text-[#B04832] hover:bg-[#B04832]/10 px-0">Tekrar dene</Button>
          </div>
        </div>
      )}

      {data && !loading && (
        <div className="card-organic overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6DCCF] bg-[#F6F1EA]">
            <span className="text-sm font-medium text-[#2E2620]">
              {view === 'calm' ? 'En Sakin ve Dengeli 50 Hisse' : 'Son Saatlerde En Çok Hareketlenen 50 Hisse'}
            </span>
            <span className="text-xs text-[#7A6A5C]">
              {view === 'calm' && data.calm_universe ? `${data.calm_universe} likit hisse içinde · ` : ''}{data.total_scanned} sembol tarandı
            </span>
          </div>
          {view === 'calm' && !(data.calm_results && data.calm_results.length) ? (
            <p className="px-4 py-6 text-sm text-[#7A6A5C]">
              Bu liste için veri yok. Önbellekteki tarama yeni sürümden önce yapılmış olabilir; "Yenile" ile tekrar tarayın.
            </p>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E6DCCF] text-xs text-[#7A6A5C] uppercase tracking-wide">
                  <th className="text-left px-4 py-2.5 font-medium w-10">#</th>
                  <th className="text-left px-4 py-2.5 font-medium">Sembol</th>
                  <th className="text-left px-4 py-2.5 font-medium">{view === 'calm' ? 'Sakin-Dengeli Skor' : 'Momentum Skoru'}</th>
                  <th className="text-right px-4 py-2.5 font-medium">Son Fiyat</th>
                  <th className="text-right px-4 py-2.5 font-medium">{view === 'calm' ? 'Saatlik Oynaklık' : 'Hacim'}</th>
                  <th className="text-right px-4 py-2.5 font-medium w-10"></th>
                </tr>
              </thead>
              <tbody>
                {(view === 'calm' ? data.calm_results : data.results).map((row, idx) => {
                  const isOpen = selectedSymbol === row.symbol;
                  return (
                    <>
                      <tr key={row.symbol} onClick={() => handleRowClick(row.symbol)}
                        className={`border-b border-[#E6DCCF] cursor-pointer transition-colors ${isOpen ? 'bg-[#F0E8DC]' : 'hover:bg-[#F6F1EA]'}`}>
                        <td className="px-4 py-3 text-[#7A6A5C] tabular-nums">{idx + 1}</td>
                        <td className="px-4 py-3 font-semibold text-[#2E2620]">{row.symbol}</td>
                        <td className="px-4 py-3">
                          {view === 'calm' ? (
                            <Badge className="bg-[#6D7C3B]/15 text-[#6D7C3B] border-[#6D7C3B]/30 font-semibold">
                              %{Number(row.calm_percentile).toFixed(0)}
                            </Badge>
                          ) : (
                            <ScoreBadge score={row.score} />
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-[#2E2620] tabular-nums">{formatPrice(row.last_close)}</td>
                        <td className="px-4 py-3 text-right text-[#7A6A5C] tabular-nums">
                          {view === 'calm' ? `%${Number(row.volatility_pct).toFixed(2)}` : formatVolume(row.last_volume)}
                        </td>
                        <td className="px-4 py-3 text-right text-[#7A6A5C]">
                          {isOpen ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr key={`${row.symbol}-chart`} className="bg-[#F6F1EA]">
                          <td colSpan={6} className="px-4 py-4">
                            <p className="text-xs font-semibold text-[#2E2620] mb-2">{row.symbol} — Saatlik Grafik (Son 60 Gün)</p>
                            <AnalysisCandlestickChart data={chartData} loading={chartLoading} height={280} showTime={true} />
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {!data && !loading && !error && (
        <div className="card-organic p-12 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-[#E8D9C7] flex items-center justify-center mx-auto">
            <Clock className="w-7 h-7 text-[#C86F4A]" />
          </div>
          <div>
            <p className="font-semibold text-[#2E2620]">Momentum taraması henüz başlatılmadı</p>
            <p className="text-sm text-[#7A6A5C] mt-1">Tarama aynı zamanda "Benzer Hisse" sekmesi için veri önbellekler</p>
          </div>
          <Button onClick={() => runScan(false)} className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white">
            <TrendingUp className="w-4 h-4 mr-2" />Tarama Başlat
          </Button>
        </div>
      )}
      </>)}
    </div>
  );
}

// ─── Tab 2: Benzer Hisse Bul ─────────────────────────────────────────────────

function DatePickerPopover({ label, value, onChange, min, max }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2 border border-[#E6DCCF] rounded-lg px-3 py-2 text-sm text-[#2E2620] hover:bg-[#F6F1EA] transition-colors">
          <Calendar className="w-3.5 h-3.5 text-[#7A6A5C]" />
          <span className="text-[#7A6A5C] text-xs mr-1">{label}:</span>
          {value ? format(value, 'dd MMM yyyy', { locale: tr }) : 'Seç'}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <DayPicker
          mode="single"
          selected={value}
          onSelect={(d) => { if (d) { onChange(d); setOpen(false); } }}
          disabled={{ before: min, after: max }}
          locale={tr}
        />
      </PopoverContent>
    </Popover>
  );
}

function SimilarTab({ getAuthHeader }) {
  const [symbols, setSymbols]       = useState([]);
  const [symInput, setSymInput]     = useState('');
  const [symOpen, setSymOpen]       = useState(false);
  const [selectedSym, setSelectedSym] = useState(null);

  const [chartData, setChartData]   = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  const today = new Date();
  const minDate = subDays(today, 59);
  const [startDate, setStartDate]   = useState(subDays(today, 4));
  const [endDate, setEndDate]       = useState(today);

  const [loading, setLoading]       = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(null);
  const [error, setError]           = useState(null);
  const [results, setResults]       = useState(null);

  const [expandedSym, setExpandedSym] = useState(null);
  const [expandChart, setExpandChart] = useState(null);
  const [expandLoading, setExpandLoading] = useState(false);

  // Sembol listesini yükle
  useEffect(() => {
    axios.get(`${API_URL}/stocks/symbols`, { headers: getAuthHeader() })
      .then(r => setSymbols(r.data.symbols || []))
      .catch(() => {});
  }, [getAuthHeader]);

  const filteredSymbols = symInput.length > 0
    ? symbols.filter(s => s.toLowerCase().includes(symInput.toLowerCase())).slice(0, 40)
    : symbols.slice(0, 40);

  const selectSymbol = async (sym) => {
    setSelectedSym(sym);
    setSymInput(sym);
    setSymOpen(false);
    setResults(null);
    setError(null);
    setChartLoading(true);
    setChartData(null);
    try {
      const res = await axios.get(
        `${API_URL}/stocks/${sym}/candlestick?interval=1h&period=60d`,
        { headers: getAuthHeader(), timeout: 30_000 }
      );
      setChartData(toIstanbul(res.data?.candles));
    } catch { setChartData(null); }
    finally { setChartLoading(false); }
  };

  const findSimilar = async () => {
    if (!selectedSym) return;
    setLoading(true);
    setLoadingMsg('Saatlik veriler kontrol ediliyor…');
    setError(null);
    setResults(null);
    setExpandedSym(null);
    try {
      // Backend cache yoksa otomatik scan çalıştırır (~2-3 dk),
      // bu yüzden timeout uzun tutuldu.
      const res = await axios.post(
        `${API_URL}/admin/hourly-pattern-match`,
        {
          symbol: selectedSym,
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
          limit: 15,
        },
        { headers: getAuthHeader(), timeout: 360_000 }
      );
      setResults(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Benzer hisse taranırken hata oluştu.');
    } finally {
      setLoading(false);
      setLoadingMsg(null);
    }
  };

  const toggleExpand = async (sym) => {
    if (expandedSym === sym) { setExpandedSym(null); setExpandChart(null); return; }
    setExpandedSym(sym);
    setExpandLoading(true);
    setExpandChart(null);
    try {
      const res = await axios.get(
        `${API_URL}/stocks/${sym}/candlestick?interval=1h&period=60d`,
        { headers: getAuthHeader(), timeout: 30_000 }
      );
      setExpandChart(toIstanbul(res.data?.candles));
    } catch { setExpandChart(null); }
    finally { setExpandLoading(false); }
  };

  const inputRef = useRef(null);

  return (
    <div className="space-y-4">
      <p className="text-sm text-[#7A6A5C]">
        Bir hisse ve tarih aralığı seçin, o aralıktaki saatlik paterni tüm BIST sembollerinde arayın.
      </p>

      {/* Hisse seçimi */}
      <div className="card-organic p-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A6A5C] z-10 pointer-events-none" />
          <Input
            ref={inputRef}
            value={symInput}
            onChange={e => { setSymInput(e.target.value.toUpperCase()); setSymOpen(true); }}
            onFocus={() => setSymOpen(true)}
            onBlur={() => setTimeout(() => setSymOpen(false), 150)}
            placeholder="Hisse ara (örn. THYAO)"
            className="pl-9 border-[#E6DCCF] bg-white text-[#2E2620]"
          />
          {symOpen && filteredSymbols.length > 0 && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-[#E6DCCF] rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {filteredSymbols.map(s => (
                <button
                  key={s}
                  onMouseDown={e => { e.preventDefault(); selectSymbol(s); }}
                  className="w-full text-left px-3 py-2 text-sm text-[#2E2620] hover:bg-[#F6F1EA] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Grafik */}
        {selectedSym && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-[#2E2620]">{selectedSym} — Saatlik Grafik (Son 60 Gün)</p>
              <p className="text-xs text-[#7A6A5C]">Mumun üzerine tıkla: 1. tıklama başlangıç · 2. tıklama bitiş</p>
            </div>
            <AnalysisCandlestickChart
              data={chartData}
              loading={chartLoading}
              height={260}
              showTime={true}
              onRangeSelect={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
          </>
        )}

        {/* Tarih aralığı + buton */}
        {selectedSym && (
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <DatePickerPopover label="Başlangıç" value={startDate} onChange={d => { setStartDate(d); if (d > endDate) setEndDate(d); }} min={minDate} max={endDate} />
            <DatePickerPopover label="Bitiş" value={endDate} onChange={d => { setEndDate(d); if (d < startDate) setStartDate(d); }} min={startDate} max={today} />
            <div className="ml-auto flex flex-col items-end gap-1">
              <Button onClick={findSimilar} disabled={loading || !selectedSym}
                className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white">
                {loading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Yükleniyor…</>
                  : <><Search className="w-4 h-4 mr-2" />Benzer Hisse Bul</>}
              </Button>
              {loading && loadingMsg && (
                <span className="text-xs text-[#7A6A5C]">{loadingMsg}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Hata */}
      {error && !loading && (
        <div className="card-organic p-5 flex items-start gap-3 border-[#B04832]/30 bg-[#B04832]/5">
          <AlertCircle className="w-5 h-5 text-[#B04832] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#B04832]">{error}</p>
        </div>
      )}

      {/* Sonuçlar */}
      {results && !loading && (
        <div className="card-organic overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6DCCF] bg-[#F6F1EA]">
            <span className="text-sm font-medium text-[#2E2620]">
              Benzer Hisseler — {format(startDate, 'dd MMM', { locale: tr })} / {format(endDate, 'dd MMM', { locale: tr })} aralığı
            </span>
            <span className="text-xs text-[#7A6A5C]">{results.symbols_with_cache} sembol karşılaştırıldı</span>
          </div>

          {results.results.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#7A6A5C]">
              Eşik değerinin üzerinde benzer hisse bulunamadı. Farklı bir tarih aralığı deneyin.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E6DCCF] text-xs text-[#7A6A5C] uppercase tracking-wide">
                    <th className="text-left px-4 py-2.5 font-medium w-10">#</th>
                    <th className="text-left px-4 py-2.5 font-medium">Sembol</th>
                    <th className="text-left px-4 py-2.5 font-medium">Benzerlik</th>
                    <th className="text-right px-4 py-2.5 font-medium w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((row, idx) => {
                    const isOpen = expandedSym === row.symbol;
                    return (
                      <>
                        <tr key={row.symbol} onClick={() => toggleExpand(row.symbol)}
                          className={`border-b border-[#E6DCCF] cursor-pointer transition-colors ${isOpen ? 'bg-[#F0E8DC]' : 'hover:bg-[#F6F1EA]'}`}>
                          <td className="px-4 py-3 text-[#7A6A5C] tabular-nums">{idx + 1}</td>
                          <td className="px-4 py-3 font-semibold text-[#2E2620]">{row.symbol}</td>
                          <td className="px-4 py-3"><SimilarityBadge pct={row.similarity} /></td>
                          <td className="px-4 py-3 text-right text-[#7A6A5C]">
                            {isOpen ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr key={`${row.symbol}-chart`} className="bg-[#F6F1EA]">
                            <td colSpan={4} className="px-4 py-4">
                              <p className="text-xs font-semibold text-[#2E2620] mb-2">{row.symbol} — Saatlik Grafik (Son 60 Gün)</p>
                              <AnalysisCandlestickChart data={expandChart} loading={expandLoading} height={260} showTime={true} />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Ana sayfa ───────────────────────────────────────────────────────────────

export default function HourlyAnalysisPage() {
  const { getAuthHeader } = useAuth();

  return (
    <div className="min-h-screen bg-[#F6F1EA]">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2E2620] font-['Playfair_Display']">Saatlik Analiz</h1>
            <p className="text-[#7A6A5C] mt-2">
              BIST hisselerini saatlik verilerle analiz edin
            </p>
          </div>

      <Tabs defaultValue="similar">
        <TabsList className="bg-[#F6F1EA] border border-[#E6DCCF]">
          <TabsTrigger value="similar" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Timer className="w-4 h-4 mr-1.5" />
            Benzer Hisse Bul
          </TabsTrigger>
          <TabsTrigger value="momentum" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <TrendingUp className="w-4 h-4 mr-1.5" />
            Momentum Sıralaması
          </TabsTrigger>
        </TabsList>

        <TabsContent value="similar" className="mt-4">
          <SimilarTab getAuthHeader={getAuthHeader} />
        </TabsContent>

        <TabsContent value="momentum" className="mt-4">
          <MomentumTab getAuthHeader={getAuthHeader} />
        </TabsContent>
      </Tabs>
        </div>
      </main>
    </div>
  );
}
